import { AccessLevel } from '../access/types';

// NOTE: every enum here but CoverageListKey is mirrored by a CHECK constraint in
// 20260924150000-create-entitlement-tables.js, 20260930150000-evolve-entitlement-schema-for-agreements.js,
// 20261006090000-add-entitlement-rule-item-kind.js, 20261009090000-add-entitlement-request-lifecycle.js or
// 20261018090000-add-entitlement-custody-groups.js; widening one needs a migration.

export enum EntitlementKind {
  PRODUCT = 'product',
  COVERAGE = 'coverage',
  SEGMENT = 'segment',
  LIMIT = 'limit',
  MANAGEMENT = 'management',
  ROLLOUT = 'rollout',
}

/**
 * What a row is about. A row with no scope (`scopeType` and `scopeId` both null) is
 * organisation-wide. `organization` is a scope in its own right: the organisation a management
 * row acts on (MANAGE_SUBSCRIPTIONS, CREATE_COMPANIES), and `user_group` the group MANAGE_USERS
 * acts on.
 */
export enum EntitlementScopeType {
  ORGANIZATION = 'organization',
  COMPANY = 'company',
  COMPANY_GROUP = 'company_group',
  PORTFOLIO = 'portfolio',
  VARIANT_SCENARIO = 'variant_scenario',
  SOURCE = 'source',
  USER_GROUP = 'user_group',
}

export enum EntitlementSource {
  CONTRACT = 'contract',
  TRIAL = 'trial',
  FREE_KIT = 'free_kit',
  OVERRIDE = 'override',
  // Written by the derivation from the legacy tables, replaced at verification.
  LEGACY = 'legacy',
  // What an organisation's members may exercise on its own resources once a custody group owns
  // them. Written only by the custody commands; never a sale.
  CUSTODY = 'custody',
}

/**
 * The sources of a commercial agreement: every source but `legacy`, which only the derivation
 * writes, and `custody`, which only the custody commands write.
 */
export type AgreementSource = Exclude<EntitlementSource, EntitlementSource.LEGACY | EntitlementSource.CUSTODY>;

export const agreementSources: readonly AgreementSource[] = [
  EntitlementSource.CONTRACT,
  EntitlementSource.TRIAL,
  EntitlementSource.FREE_KIT,
  EntitlementSource.OVERRIDE,
];

/** `open` until a command releases it, Ops declines it or its requester withdraws it; each is final. */
export enum EntitlementRequestStatus {
  OPEN = 'open',
  RELEASED = 'released',
  DECLINED = 'declined',
  WITHDRAWN = 'withdrawn',
}

export enum CoverageListObjectType {
  COMPANY = 'company',
  COMPANY_GROUP = 'company_group',
  PORTFOLIO = 'portfolio',
  // The CSRD results of a company, group or portfolio, read without the entity itself.
  CSRD_RESULT = 'csrd_result',
}

/**
 * Who reaches a sponsor's items: every member (the default policy, the baseline), one guest of
 * another organisation, or the members assigned a shared member policy on top of the baseline.
 * `custody_owner` ("Owners") has no rules: its assignees are the members of the organisation's
 * custody group, and own what that group owns.
 */
export enum EntitlementAssignmentPolicyKind {
  ORGANIZATION_DEFAULT = 'organization_default',
  SPONSORED_GUEST = 'sponsored_guest',
  MEMBER = 'member',
  CUSTODY_OWNER = 'custody_owner',
}

/** Whose canonical result context a rule's results come from. Later kinds add a reference column. */
export enum EntitlementResultContextKind {
  SPONSOR = 'sponsor',
}

/**
 * What a rule passes on. A `coverage` rule passes coverage items, which publication projects into
 * `access` rows; a `rights` rule passes product and limit items, which the rights reader reads live.
 */
export enum EntitlementPolicyRuleItemKind {
  COVERAGE = 'coverage',
  RIGHTS = 'rights',
}

export enum EntitlementRecipientBindingKind {
  // Every member of a structural group, the root group for the default policy.
  MEMBERSHIP = 'membership',
  // One named user, without any `user_group_user` row.
  USER = 'user',
}

export enum EntitlementHolderPathRetiredReason {
  EXPIRED = 'expired',
  REVOKED = 'revoked',
  // A scoped rule's path left with nothing of its item in the rule's scope; published again when rows return.
  EMPTIED = 'emptied',
}

/**
 * A `fixed` list is one sponsor's set of resources sold together; a `selection` is one sponsor's set
 * of resources a policy rule is scoped to, never sold; a `custody` list is an organisation's own
 * resources at the level its members may exercise, carried by its custody item; `shared` lists are
 * the rest.
 */
export enum CoverageListIntent {
  FIXED = 'fixed',
  SHARED = 'shared',
  SELECTION = 'selection',
  CUSTODY = 'custody',
}

/** The tables the projector writes to, and so the tables its ledger points into. */
export enum EntitlementProjectionTargetTable {
  ACCESS = 'access',
  PRIVILEGE = 'privilege',
  USER_GROUP_USER_GROUP = 'user_group_user_group',
}

export enum EntitlementProjectionOrigin {
  // Written by a published holder path.
  PUBLISHED = 'published',
  // A legacy row deleted at activation, kept as evidence of the item that took it over.
  ADOPTED = 'adopted',
}

// Values of `coverage_list.key`, which is data: no CHECK constraint stands behind them.
export enum CoverageListKey {
  STANDARD = 'standard',
  EXTENDED = 'extended',
}

/** Key of an organisation's own list; there is at most one per organisation. */
export function toCustomCoverageListKey(organizationId: number): string {
  return `custom:${organizationId}`;
}

// Any JSON object; the derivation that writes it decides its shape.
export type EntitlementJson = Record<string, unknown>;

/**
 * The value of an agreement's `limit` item: the quota and, optionally, its rolling window in days.
 * A legacy mirror row keeps the derivation's shape of a quota override instead.
 */
export interface AgreementLimitValue {
  limit: number;
  windowDays?: number;
}

/**
 * An agreement an organization has now through its membership binding: what makes it a client
 * (`contract`) or a trial user (`trial`), whatever its items. `effectiveUntil` is the earlier of the
 * agreement's and the binding's ends; null is open-ended.
 */
export interface LiveAgreement {
  source: AgreementSource;
  effectiveUntil: Date | null;
}

/**
 * Null `scopeType` and `scopeId` together mean organisation-wide. `scopeCompanyId` is set on
 * `variant_scenario` rows and only there: scenario ids are shared across companies, so the
 * (company, scenario) pair is the key.
 */
export interface EntitlementScope {
  scopeType: EntitlementScopeType | null;
  scopeId: string | null;
  scopeCompanyId: string | null;
}

/** What an entitlement or a request for one is about, without who holds it. */
export type EntitlementTarget = EntitlementScope & {
  kind: EntitlementKind;
  key: string;
};

/**
 * A row is a legacy row (`source = legacy`, no agreement) or an agreement item. Items of different
 * agreements never merge, so a trial and a contract on the same key and scope are two rows. An
 * item's own window narrows its agreement's; authorization reads the intersection.
 */
export type Entitlement = EntitlementTarget & {
  organizationId: number;
  // Null exactly on `legacy` rows. An item's source equals its agreement's.
  agreementId: string | null;
  // Set on agreement items, and stable within the agreement; null on legacy rows.
  itemKey: string | null;
  // Null: the whole organisation. Set: one member. Always null on agreement items, which never
  // encode holders or assignments.
  subjectUserId: number | null;
  // Null: the default home (root group, admin child for management). Set: another consumer group.
  // Always null on agreement items.
  subjectUserGroupId: number | null;
  // A single legacy level, never a read/edit pair: the projector reproduces it byte for byte.
  level: AccessLevel | null;
  value: EntitlementJson | null;
  source: EntitlementSource;
  // For derived rows: the legacy rows and the ancestry path that produced them.
  legacyRef: EntitlementJson | null;
  reason: string | null;
  validFrom: Date | null;
  validUntil: Date | null;
};

/** Revocation is terminal for the revoked identity; reinstatement creates a new one. */
export interface EntitlementRevocation {
  revokedAt: Date | null;
  revokedByUserId: number | null;
  revocationReason: string | null;
}

/** What only commands change: `revision` advances on every accepted command that changes the row. */
export type EntitlementRevisionState = EntitlementRevocation & { revision: number };

/** The columns of an entitlement, agreement or policy that are not part of its create shape. */
export type EntitlementMetadata = EntitlementRevisionState & {
  id: string;
  // Required on create; null only once the user who created the row has been deleted.
  createdByUserId: number | null;
  createdAt: Date;
  updatedAt: Date;
};

/** One commercial lifecycle of a sponsor. Two agreements with the same source coexist. */
export interface EntitlementAgreement {
  // The sponsor. Never re-derived from a recipient.
  organizationId: number;
  source: AgreementSource | EntitlementSource.CUSTODY;
  // A contract or trial reference, informational.
  externalRef: string | null;
  validFrom: Date;
  // The enforcement deadline, grace included. Null: open-ended.
  validUntil: Date | null;
  // Informational end before grace; never read by authorization.
  termEndsAt: Date | null;
}

/** Who of the sponsor's world receives its items. Null `activatedAt` is shadow: unreachable. */
export interface EntitlementAssignmentPolicy {
  organizationId: number;
  kind: EntitlementAssignmentPolicyKind;
  activatedAt: Date | null;
  // Set exactly on member policies.
  name: string | null;
  // The member policy an inviter sees preselected; at most one unrevoked per sponsor.
  isInvitationDefault: boolean;
}

/**
 * Which of the sponsor's items a policy passes on, at what cap. A null scope is every item (the
 * default policy); a set scope is one resource, or a selection list of the sponsor (`scopeListId`).
 */
export type EntitlementPolicyRule = EntitlementScope & {
  policyId: string;
  organizationId: number;
  // A `selection` list of the sponsor; set only on a coverage rule with no other scope.
  scopeListId: string | null;
  // Null: the item's own level.
  levelCap: AccessLevel | null;
  resultContextKind: EntitlementResultContextKind;
  // A `rights` rule's scope is null.
  itemKind: EntitlementPolicyRuleItemKind;
};

/** The recipient of a policy: members of a structural group, or one guest user. */
export interface EntitlementRecipientBinding {
  policyId: string;
  organizationId: number;
  kind: EntitlementRecipientBindingKind;
  // Set exactly on `membership` bindings.
  memberOfUserGroupId: number | null;
  // Set exactly on `user` bindings.
  recipientUserId: number | null;
  validFrom: Date;
  validUntil: Date | null;
}

/**
 * The columns of a rule or binding that are not part of its create shape. Revocation carries no
 * actor or reason here: the command history holds them.
 */
export interface EntitlementRuleMetadata {
  id: string;
  revokedAt: Date | null;
  revision: number;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * A `user_group` of type `entitlement_holder` that holds the `access` rows of one item and rule,
 * with its recorded sponsor. It has no membership or parent-link rows, so no structural loader or
 * descendant walk reaches it.
 */
export interface EntitlementHolder {
  userGroupId: number;
  organizationId: number;
}

/**
 * A member policy's group: a `user_group` of type `entitlement_policy_group`, linked once directly
 * under its sponsor's root group. Its members are the users assigned the policy. It holds no rows.
 */
export interface EntitlementPolicyGroup {
  userGroupId: number;
  organizationId: number;
  policyId: string;
}

/**
 * An organisation's custody group: a `user_group` of type `entitlement_custody_group`, linked once
 * directly under its root. It holds the organisation's `o` rows on its own funds and portfolios, and
 * its members are the assignees of the organisation's `custody_owner` policy. One per organisation,
 * kept when custody returns to the root.
 */
export interface EntitlementCustodyGroup {
  userGroupId: number;
  organizationId: number;
}

/**
 * The authorization edge from a rule to a holder for one item. It carries no copy of validity:
 * authorization reads validity and revocation live from the item, agreement, rule, policy and binding.
 */
export interface EntitlementHolderPath {
  organizationId: number;
  policyRuleId: string;
  entitlementId: string;
  holderUserGroupId: number;
  // The item revision the path was published from.
  publishedRevision: number;
  publishedAt: Date;
  retiredAt: Date | null;
  retiredReason: EntitlementHolderPathRetiredReason | null;
}

/** An accepted, committed command; replaying its id returns `result`. */
export interface EntitlementCommandRecord {
  // The caller's command id.
  id: string;
  organizationId: number;
  type: string;
  // sha256 of the canonical command without its id.
  requestHash: string;
  actorUserId: number | null;
  acceptedAt: Date;
  result: EntitlementJson;
  requestId: string | null;
}

/** Advanced by every accepted command of the sponsor as its last statement. */
export interface EntitlementSponsorRevision {
  organizationId: number;
  revision: number;
}

export type EntitlementRequest = EntitlementTarget & {
  organizationId: number;
  requestedByUserId: number | null;
  // A company the customer names that is not in the database: a `coverage:entity` request with no
  // scope until it is granted.
  requestedName: string | null;
  note: string | null;
  status: EntitlementRequestStatus;
  decidedByUserId: number | null;
  decidedAt: Date | null;
  decisionReason: string | null;
  fulfilledAgreementId: string | null;
  // The agreement item that released the request; its state is read from this item alone.
  fulfilledEntitlementId: string | null;
  // History only: the command row is written after the request is released, so it has no FK.
  fulfilledCommandId: string | null;
};

export interface CoverageList {
  key: string;
  name: string;
  description: string | null;
  // The organisation whose list it is: set on `custom:<organizationId>` lists and fixed lists, null
  // on the Upright-managed `standard` and `extended`.
  ownerOrganizationId: number | null;
  intent: CoverageListIntent;
}

export interface CoverageListItem {
  listId: string;
  objectType: CoverageListObjectType;
  objectId: string;
  level: AccessLevel;
}

export interface EntitlementEvent {
  organizationId: number;
  entitlementId: string | null;
  requestId: string | null;
  commandId: string | null;
  agreementId: string | null;
  action: string;
  before: EntitlementJson | null;
  after: EntitlementJson | null;
  actorUserId: number | null;
}

/**
 * One contribution to a target row. Several can support one row; the row is deleted only when its
 * last unretired contribution is retired.
 */
export interface EntitlementProjection {
  entitlementId: string;
  origin: EntitlementProjectionOrigin;
  // Set exactly on `published` contributions.
  policyRuleId: string | null;
  holderUserGroupId: number | null;
  pathId: string | null;
  targetTable: EntitlementProjectionTargetTable;
  // The target row's natural key: `<objectType>|<objectId>|<subjectType>|<subjectId>` for access rows.
  targetKey: string;
  // Set exactly on `adopted` contributions: the deleted legacy row.
  legacyRow: EntitlementJson | null;
  retiredAt: Date | null;
}
